import React, { useEffect, useState, useRef } from 'react';
import { productsAPI, stockAPI } from '../services/api';
import { Product } from '../types';
import { useAuth } from '../contexts/AuthContext';
import ProductAttrs from '../components/ProductAttrs';
import './Stock.css';

const unitLabel = (u: string) => u === 'PIECE' ? 'шт.' : u === 'METER' ? 'м.' : 'кг.';

const StockPage: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [editId, setEditId] = useState<number | null>(null);
  const [editMode, setEditMode] = useState<'set' | 'increase' | 'decrease'>('increase');
  const [editAmount, setEditAmount] = useState('');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const { isAdmin } = useAuth();

  useEffect(() => { fetchProducts(); }, []);

  useEffect(() => {
    if (editId !== null) inputRef.current?.focus();
  }, [editId]);

  const fetchProducts = async () => {
    try {
      const data = await productsAPI.getAll({ limit: 1000 });
      setProducts(data.data);
    } finally {
      setLoading(false);
    }
  };

  const openEdit = (product: Product, mode: 'set' | 'increase' | 'decrease') => {
    setEditId(product.id);
    setEditMode(mode);
    setEditAmount('');
  };

  const cancelEdit = () => {
    setEditId(null);
    setEditAmount('');
  };

  const handleSave = async (product: Product) => {
    const qty = parseFloat(editAmount);
    if (isNaN(qty) || qty < 0) { alert('Введите корректное число'); return; }
    setSaving(true);
    try {
      if (editMode === 'set') await stockAPI.set(product.id, qty);
      else if (editMode === 'increase') await stockAPI.increase(product.id, qty);
      else await stockAPI.decrease(product.id, qty);
      cancelEdit();
      await fetchProducts();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Ошибка');
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent, product: Product) => {
    if (e.key === 'Enter') handleSave(product);
    if (e.key === 'Escape') cancelEdit();
  };

  const categories = Array.from(
    new Map(products.filter(p => p.category).map(p => [p.category!.id, p.category!])).values()
  ).sort((a, b) => a.name.localeCompare(b.name));

  const filtered = products.filter(p => {
    const matchSearch = p.name.toLowerCase().includes(search.toLowerCase());
    const matchCategory = !categoryFilter || String(p.category?.id) === categoryFilter;
    return matchSearch && matchCategory;
  });

  const stockQty = (p: Product) => Number(p.stock?.quantity ?? 0);
  const qtyClass = (p: Product) => {
    const q = stockQty(p);
    if (q === 0) return 'qty-zero';
    if (q < 10) return 'qty-low';
    return 'qty-ok';
  };

  const lowCount = products.filter(p => stockQty(p) < 10).length;
  const zeroCount = products.filter(p => stockQty(p) === 0).length;

  if (loading) return <div className="stock-loading">Загрузка...</div>;

  return (
    <div className="stock-page">
      <div className="stock-header">
        <h1>Склад</h1>
        <div className="stock-summary">
          <span className="summary-chip chip-total">{products.length} товаров</span>
          {zeroCount > 0 && <span className="summary-chip chip-zero">{zeroCount} нет в наличии</span>}
          {lowCount > 0 && <span className="summary-chip chip-low">{lowCount} мало</span>}
        </div>
      </div>

      <div className="stock-toolbar">
        <input
          className="stock-search"
          type="text"
          placeholder="Поиск по названию..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select
          className="stock-category-select"
          value={categoryFilter}
          onChange={e => setCategoryFilter(e.target.value)}
        >
          <option value="">Все категории</option>
          {categories.map(c => (
            <option key={c.id} value={String(c.id)}>{c.name}</option>
          ))}
        </select>
      </div>

      {/* Desktop Stock Table */}
      <div className="stock-table-wrap stock-desktop-table">
        <table className="stock-table">
          <thead>
            <tr>
              <th>Товар</th>
              <th>Категория</th>
              <th>Ед.</th>
              <th className="th-qty">Остаток</th>
              {isAdmin() && <th className="th-actions">Управление</th>}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={isAdmin() ? 5 : 4} className="stock-empty">Ничего не найдено</td></tr>
            )}
            {filtered.map(product => (
              <tr key={product.id} className={editId === product.id ? 'row-editing' : ''}>
                <td className="td-name">
                  {product.name}
                  <ProductAttrs attributes={product.attributes} />
                </td>
                <td className="td-cat">{product.category?.name || <span style={{ color: '#9ca3af' }}>—</span>}</td>
                <td className="td-unit">{unitLabel(product.unit)}</td>
                <td className="td-qty">
                  <span className={`qty-badge ${qtyClass(product)}`}>
                    {stockQty(product).toLocaleString('ru-RU', { maximumFractionDigits: 2 })}
                  </span>
                </td>
                {isAdmin() && (
                  <td className="td-actions">
                    {editId === product.id ? (
                      <div className="inline-edit">
                        <select
                          className="edit-mode-select"
                          value={editMode}
                          onChange={e => setEditMode(e.target.value as any)}
                        >
                          <option value="increase">+</option>
                          <option value="decrease">−</option>
                          <option value="set">=</option>
                        </select>
                        <input
                          ref={inputRef}
                          className="edit-input"
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder="0"
                          value={editAmount}
                          onChange={e => setEditAmount(e.target.value)}
                          onKeyDown={e => handleKeyDown(e, product)}
                        />
                        <button
                          className="btn-save"
                          onClick={() => handleSave(product)}
                          disabled={saving || !editAmount}
                          title="Применить"
                        >
                          ✓
                        </button>
                        <button className="btn-cancel" onClick={cancelEdit} title="Отмена">✕</button>
                      </div>
                    ) : (
                      <div className="row-btns action-buttons-cell">
                        <button className="btn-plus" onClick={() => openEdit(product, 'increase')} title="Добавить">+</button>
                        <button className="btn-minus" onClick={() => openEdit(product, 'decrease')} title="Списать">−</button>
                        <button className="btn-set" onClick={() => openEdit(product, 'set')} title="Установить">=</button>
                      </div>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Stock Cards */}
      <div className="stock-mobile-list">
        {filtered.length === 0 ? (
          <div className="stock-empty-mobile">Ничего не найдено</div>
        ) : (
          filtered.map(product => (
            <div key={product.id} className={`stock-mobile-card ${editId === product.id ? 'card-editing' : ''}`}>
              <div className="stock-card-head">
                <div className="stock-card-title-wrap">
                  <span className="stock-prod-name">{product.name}</span>
                  {product.category?.name && (
                    <span className="stock-cat-pill">{product.category.name}</span>
                  )}
                </div>
                <div className="stock-qty-wrap">
                  <span className={`qty-badge ${qtyClass(product)}`}>
                    {stockQty(product).toLocaleString('ru-RU', { maximumFractionDigits: 2 })} {unitLabel(product.unit)}
                  </span>
                </div>
              </div>

              {product.attributes && product.attributes.length > 0 && (
                <div className="stock-card-attrs">
                  <ProductAttrs attributes={product.attributes} />
                </div>
              )}

              {isAdmin() && (
                <div className="stock-card-admin-action">
                  {editId === product.id ? (
                    <div className="mobile-stock-edit-form">
                      <div className="mobile-edit-mode-wrap">
                        <select
                          className="edit-mode-select"
                          value={editMode}
                          onChange={e => setEditMode(e.target.value as any)}
                        >
                          <option value="increase">+</option>
                          <option value="decrease">−</option>
                          <option value="set">=</option>
                        </select>
                        <input
                          ref={inputRef}
                          className="edit-input"
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder="0"
                          value={editAmount}
                          onChange={e => setEditAmount(e.target.value)}
                          onKeyDown={e => handleKeyDown(e, product)}
                        />
                        <button
                          className="btn-save mobile-btn-icon-save"
                          onClick={() => handleSave(product)}
                          disabled={saving || !editAmount}
                          title="Применить"
                        >
                          {saving ? '...' : '✓'}
                        </button>
                        <button className="btn-cancel mobile-btn-icon-cancel" onClick={cancelEdit} title="Отмена">
                          ✕
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="stock-mobile-quick-actions">
                      <button className="btn-plus" onClick={() => openEdit(product, 'increase')} title="Добавить">
                        +
                      </button>
                      <button className="btn-minus" onClick={() => openEdit(product, 'decrease')} title="Списать">
                        −
                      </button>
                      <button className="btn-set" onClick={() => openEdit(product, 'set')} title="Установить">
                        =
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default StockPage;
